/**
 * Adventure Collections API Endpoints
 * API service for managing adventure collections
 */

import { ApiService } from '../apiService'
import type {
  AdventureCollectionDto,
  AdventureCollectionSummaryDto,
  CreateAdventureCollectionDto,
  UpdateAdventureCollectionDto,
  AvailableAdventureDto,
  CollectionBadgeDto,
  CreateCollectionBadgeDto,
  CollectionProgressDto,
  ImageUploadResponseDto,
  CollectionAchievementDto,
  CollectionAchievementSummaryDto,
  CollectionAchievementAdventureDto,
  CreateCollectionAchievementDto,
  UpdateCollectionAchievementDto,
  AddAchievementAdventureDto,
  AchievementProgressDto,
  AchievementProgressSummaryDto,
  CollectionMaintainerDto,
  AddCollectionMaintainerRequest,
} from '../types/adventureCollection'

export const adventureCollectionsApi = {
  // ===================
  // COLLECTION CRUD
  // ===================

  /** Create a new collection */
  create: (data: CreateAdventureCollectionDto) =>
    ApiService.post<AdventureCollectionDto>('/api/adventure-collections', data),

  /** Get a single collection by ID */
  get: (id: string) =>
    ApiService.get<AdventureCollectionDto>(`/api/adventure-collections/${id}`),

  /** Update a collection */
  update: (id: string, data: UpdateAdventureCollectionDto) =>
    ApiService.put<AdventureCollectionDto>(`/api/adventure-collections/${id}`, data),

  /** Delete a collection */
  delete: (id: string) =>
    ApiService.delete<void>(`/api/adventure-collections/${id}`),

  // ===================
  // USER'S COLLECTIONS
  // ===================

  /** Get current user's collections */
  getMyCollections: () =>
    ApiService.get<AdventureCollectionSummaryDto[]>('/api/adventure-collections/my-collections'),

  /** Get public collections */
  getPublicCollections: (page = 1, pageSize = 20) =>
    ApiService.get<AdventureCollectionSummaryDto[]>(
      `/api/adventure-collections/public?page=${page}&pageSize=${pageSize}`
    ),

  // ===================
  // COLLECTION PROGRESS
  // ===================

  /** Get progress on a specific collection */
  getCollectionProgress: (id: string) =>
    ApiService.get<CollectionProgressDto>(
      `/api/adventure-collections/${id}/progress`
    ),

  /** Get current user's progress on all collections */
  getMyCollectionProgress: () =>
    ApiService.get<CollectionProgressDto[]>(
      '/api/adventure-collections/my-progress'
    ),

  /** Check if user meets requirement for a collection */
  checkRequirement: (id: string, requiredPercentage: number) =>
    ApiService.get<boolean>(
      `/api/adventure-collections/${id}/check-requirement?requiredPercentage=${requiredPercentage}`
    ),

  // ===================
  // ADVENTURES (for achievements)
  // ===================

  /** Get adventures available to add to achievements */
  getAvailableAdventures: (collectionId: string) =>
    ApiService.get<AvailableAdventureDto[]>(
      `/api/adventure-collections/${collectionId}/available-adventures`
    ),

  // ===================
  // COLLECTION BADGE (one per collection)
  // ===================

  /** Set/update the badge for a collection (PUT - creates or updates) */
  setBadge: (collectionId: string, data: CreateCollectionBadgeDto) =>
    ApiService.put<CollectionBadgeDto>(
      `/api/adventure-collections/${collectionId}/badge`,
      data
    ),

  /** Delete the collection badge */
  deleteBadge: (collectionId: string) =>
    ApiService.delete<void>(
      `/api/adventure-collections/${collectionId}/badge`
    ),

  /** Upload badge image */
  uploadBadgeImage: async (file: File): Promise<ImageUploadResponseDto> => {
    const formData = new FormData()
    formData.append('file', file)
    return ApiService.upload<ImageUploadResponseDto>(
      '/api/adventure-collections/badges/image',
      formData
    )
  },

  /** Get current user's earned badges */
  getMyBadges: () =>
    ApiService.get<CollectionBadgeDto[]>(
      '/api/adventure-collections/my-badges'
    ),

  // ===================
  // COLLECTION ACHIEVEMENTS
  // ===================

  /** Get all achievements for a collection */
  getAchievements: (collectionId: string) =>
    ApiService.get<CollectionAchievementSummaryDto[]>(
      `/api/adventure-collections/${collectionId}/achievements`
    ),

  /** Get single achievement with adventures */
  getAchievement: (collectionId: string, achievementId: string) =>
    ApiService.get<CollectionAchievementDto>(
      `/api/adventure-collections/${collectionId}/achievements/${achievementId}`
    ),

  /** Create an achievement */
  createAchievement: (collectionId: string, data: CreateCollectionAchievementDto) =>
    ApiService.post<CollectionAchievementDto>(
      `/api/adventure-collections/${collectionId}/achievements`,
      data
    ),

  /** Update an achievement */
  updateAchievement: (collectionId: string, achievementId: string, data: UpdateCollectionAchievementDto) =>
    ApiService.put<CollectionAchievementDto>(
      `/api/adventure-collections/${collectionId}/achievements/${achievementId}`,
      data
    ),

  /** Delete an achievement */
  deleteAchievement: (collectionId: string, achievementId: string) =>
    ApiService.delete<void>(
      `/api/adventure-collections/${collectionId}/achievements/${achievementId}`
    ),

  /** Upload achievement icon */
  uploadAchievementIcon: async (collectionId: string, file: File): Promise<ImageUploadResponseDto> => {
    const formData = new FormData()
    formData.append('file', file)
    return ApiService.upload<ImageUploadResponseDto>(
      `/api/adventure-collections/${collectionId}/achievements/icon`,
      formData
    )
  },

  /** Add adventure to achievement */
  addAchievementAdventure: (collectionId: string, achievementId: string, data: AddAchievementAdventureDto) =>
    ApiService.post<CollectionAchievementDto>(
      `/api/adventure-collections/${collectionId}/achievements/${achievementId}/adventures`,
      data
    ),

  /** Remove adventure from achievement */
  removeAchievementAdventure: (collectionId: string, achievementId: string, adventureId: string) =>
    ApiService.delete<void>(
      `/api/adventure-collections/${collectionId}/achievements/${achievementId}/adventures/${adventureId}`
    ),

  /** Get adventures in achievement */
  getAchievementAdventures: (collectionId: string, achievementId: string) =>
    ApiService.get<CollectionAchievementAdventureDto[]>(
      `/api/adventure-collections/${collectionId}/achievements/${achievementId}/adventures`
    ),

  // ===================
  // USER ACHIEVEMENT PROGRESS
  // ===================

  /** Get current user's progress on a specific achievement */
  getAchievementProgress: (achievementId: string) =>
    ApiService.get<AchievementProgressDto>(
      `/api/achievements/${achievementId}/progress`
    ),

  /** Get current user's progress on all collection achievements */
  getMyAchievementProgress: () =>
    ApiService.get<AchievementProgressDto[]>(
      `/api/achievements/my-progress`
    ),

  /** Get compact progress summaries for current user */
  getMyAchievementProgressSummary: () =>
    ApiService.get<AchievementProgressSummaryDto[]>(
      `/api/achievements/my-progress/summary`
    ),

  /** Get list of achievement IDs the current user has completed */
  getCompletedAchievementIds: () =>
    ApiService.get<string[]>(
      `/api/achievements/completed`
    ),

  // ===================
  // COLLECTION MAINTAINERS
  // ===================

  /** Get all maintainers for a collection */
  getMaintainers: (collectionId: string) =>
    ApiService.get<CollectionMaintainerDto[]>(
      `/api/adventure-collections/${collectionId}/maintainers`
    ),

  /** Add a maintainer to a collection */
  addMaintainer: (collectionId: string, data: AddCollectionMaintainerRequest) =>
    ApiService.post<CollectionMaintainerDto>(
      `/api/adventure-collections/${collectionId}/maintainers`,
      data
    ),

  /** Remove a maintainer from a collection */
  removeMaintainer: (collectionId: string, maintainerId: string) =>
    ApiService.delete<void>(
      `/api/adventure-collections/${collectionId}/maintainers/${maintainerId}`
    ),
}
